import { push } from '@takeshape/redux-little-router';
import type { Workflow } from '@takeshape/schema';
import { fetchSchemaRoutine } from 'app/branches/sagas/fetch-schema';
import { getSavingErrorMessage } from 'app/common/utils/handle-error';
import { getRoutineSaga } from 'app/common/utils/new-sagas';
import { showMessage } from 'app/messages/actions';
import { showMessageSaga } from 'app/messages/saga';
import { clearSelection, initMultiSelect } from 'app/multi-select/actions';
import { getValues } from 'app/schema-form/selectors';
import { onFormSuccess } from 'app/schema-form/util/on-submit';
import {
  initWorkflowsMultiSelect,
  type RemoveWorkflowResponse,
  removeWorkflowRoutine,
  type SaveWorkflowResponse,
  saveWorkflowRoutine,
  updateWorkflowRoutine
} from 'app/workflows/actions';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getCurrentBranchPath } from '../branches/selectors.js';
import { removeNulls } from '../common/utils/remove-nulls.js';
import { getSchemaVersion } from '../schema/selectors.js';
import { createWorkflow, removeWorkflow, updateWorkflow } from './api.js';
import { DEFAULT_WORKFLOW, WORKFLOW_FORM_NAME, WORKFLOW_UPDATE_FORM } from './constants.js';
import { getWorkflowId, getWorkflowList } from './selectors.js';

export function* handleInitMultiSelect(): SagaIterator<void> {
  const workflows: Workflow[] = yield select(getWorkflowList);
  yield put(initMultiSelect({ multiSelectName: 'Workflows' })(workflows.map((workflow) => workflow.name)));
}

export function* createWorkflowSaga(payload: Workflow): SagaIterator<SaveWorkflowResponse> {
  const version = yield select(getSchemaVersion);
  const { result } = yield call(createWorkflow, {
    input: { workflow: payload, version }
  });
  yield put(fetchSchemaRoutine.requested(undefined, { runtime: true }));
  yield put(initWorkflowsMultiSelect());
  return { ...result, workflow: removeNulls(result.workflow) };
}

export function* updateWorkflowSaga(payload: Workflow): SagaIterator<SaveWorkflowResponse> {
  const version = yield select(getSchemaVersion);
  const { result } = yield call(updateWorkflow, {
    input: { workflow: payload, version, name: payload.name }
  });
  yield put(fetchSchemaRoutine.requested(undefined, { runtime: true }));
  return { ...result, workflow: removeNulls(result.workflow) };
}

export function* removeWorkflowSaga(payload: string | Set<string>): SagaIterator<RemoveWorkflowResponse[]> {
  const results = [];
  let triedToDeleteDefault = false;
  let selected: string[];
  if (typeof payload === 'string') {
    selected = [payload];
  } else {
    selected = Array.from(payload);
  }

  selected = selected.filter((name: string) => {
    if (name === DEFAULT_WORKFLOW) {
      triedToDeleteDefault = true;
    }

    return name !== DEFAULT_WORKFLOW;
  });
  for (const name of selected) {
    const version = yield select(getSchemaVersion);
    const { result } = yield call(removeWorkflow, { input: { name, version } });
    results.push(result);
    yield put(fetchSchemaRoutine.requested(undefined, { runtime: true }));
  }

  yield put(clearSelection({ multiSelectName: 'Workflows' })());
  yield put(initWorkflowsMultiSelect());
  yield put(
    showMessage(
      'info',
      `Removed ${selected.length} workflow${selected.length === 1 ? '' : 's'}${
        triedToDeleteDefault ? '. The default workflow cannot be removed.' : ' successfully'
      }`,
      4000
    )
  );
  return results;
}

export function* handleWorkflowSave(): SagaIterator {
  const workflowId = yield select(getWorkflowId);
  const isNew = !workflowId || workflowId === 'create';
  const workflowForm = yield select(getValues, {
    formName: isNew ? WORKFLOW_FORM_NAME : WORKFLOW_UPDATE_FORM
  });
  if (isNew) {
    yield put(saveWorkflowRoutine.requested(workflowForm));
  } else {
    yield put(updateWorkflowRoutine.requested(workflowForm));
  }
}

export function* postSaveRedirect(): SagaIterator {
  const branchPath = yield select(getCurrentBranchPath);
  yield put(push(`${branchPath}/workflows`));
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(initWorkflowsMultiSelect, handleInitMultiSelect),
    takeLatest(onFormSuccess(WORKFLOW_FORM_NAME), handleWorkflowSave),
    takeLatest(onFormSuccess(WORKFLOW_UPDATE_FORM), handleWorkflowSave),
    takeLatest(saveWorkflowRoutine.REQUESTED, getRoutineSaga(saveWorkflowRoutine, createWorkflowSaga)),
    takeLatest(updateWorkflowRoutine.REQUESTED, getRoutineSaga(updateWorkflowRoutine, updateWorkflowSaga)),
    takeLatest(removeWorkflowRoutine.REQUESTED, getRoutineSaga(removeWorkflowRoutine, removeWorkflowSaga)),
    takeLatest(saveWorkflowRoutine.FULFILLED, postSaveRedirect),
    takeLatest(updateWorkflowRoutine.FULFILLED, postSaveRedirect),
    takeLatest(removeWorkflowRoutine.FULFILLED, postSaveRedirect),
    takeLatest(saveWorkflowRoutine.FULFILLED, showMessageSaga('info', 'Workflow saved successfully', 2000)),
    takeLatest(updateWorkflowRoutine.FULFILLED, showMessageSaga('info', 'Workflow saved successfully', 2000)),
    takeLatest(saveWorkflowRoutine.rejected, showMessageSaga('error', getSavingErrorMessage('Saving', 'workflow'))),
    takeLatest(updateWorkflowRoutine.rejected, showMessageSaga('error', getSavingErrorMessage('Updating', 'workflow'))),
    takeLatest(removeWorkflowRoutine.rejected, showMessageSaga('error', getSavingErrorMessage('Removing', 'workflow')))
  ]);
}
